import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { t } from '@/i18n'
import {
  createSnapshot,
  getAccounts,
  getInventoryLots,
  getInventoryPositions,
  getInventorySnapshots,
} from '@/lib/api'
import { formatDateTime, formatMoney, formatQty } from '@/lib/format'

export function Inventory() {
  const queryClient = useQueryClient()
  const positions = useQuery({ queryKey: ['inventory', 'positions'], queryFn: getInventoryPositions })
  const lots = useQuery({ queryKey: ['inventory', 'lots'], queryFn: () => getInventoryLots() })
  const snapshots = useQuery({ queryKey: ['inventory', 'snapshots'], queryFn: getInventorySnapshots })
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: getAccounts })
  const [accountId, setAccountId] = useState('')
  const [asset, setAsset] = useState('USDT')
  const [actual, setActual] = useState('')
  const snapshot = useMutation({
    mutationFn: () =>
      createSnapshot({
        account_id: accountId,
        asset_code: asset,
        actual_quantity: actual,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      setActual('')
    },
  })

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 md:px-8 md:py-8">
      <h1 className="text-xl font-semibold text-slate-100">{t('inv.title')}</h1>
      <p className="mt-1 text-sm text-slate-500">{t('inv.lead')}</p>

      <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5">
        <h2 className="text-sm font-medium text-slate-300">{t('inv.open')}</h2>
        {positions.data?.length ? (
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-xs text-slate-500">
              <tr>
                <th className="py-1">{t('common.asset')}</th>
                <th className="py-1 text-right">{t('inv.remain')}</th>
                <th className="py-1 text-right">{t('inv.lots')}</th>
                <th className="py-1 text-right">{t('inv.avg')}</th>
              </tr>
            </thead>
            <tbody>
              {positions.data.map((row) => (
                <tr key={row.asset_code} className="border-t border-surface-border/60">
                  <td className="py-2">{row.asset_code}</td>
                  <td className="num py-2 text-right">{formatQty(row.quantity_remaining)}</td>
                  <td className="num py-2 text-right">{row.open_lots}</td>
                  <td className="num py-2 text-right">
                    {row.avg_unit_cost && row.cost_currency
                      ? formatMoney(row.avg_unit_cost, row.cost_currency)
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="mt-3 text-sm text-slate-500">{t('inv.noLots')}</p>
        )}
      </section>

      <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5">
        <h2 className="text-sm font-medium text-slate-300">{t('inv.fifo')}</h2>
        <ul className="mt-3 divide-y divide-surface-border text-sm">
          {(lots.data ?? []).map((lot) => (
            <li key={lot.id} className="flex justify-between gap-4 py-2">
              <div>
                <div className="text-slate-200">
                  {lot.asset_code}
                  {lot.is_opening_balance ? t('inv.opening') : ''}
                </div>
                <div className="text-xs text-slate-500">{formatDateTime(lot.acquired_at)}</div>
              </div>
              <div className="num text-right text-slate-300">
                {formatQty(lot.quantity_remaining)} / {formatQty(lot.quantity_initial)}
                <div className="text-xs text-slate-500">
                  {formatMoney(lot.unit_cost, lot.cost_currency)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5">
        <h2 className="text-sm font-medium text-slate-300">{t('inv.check')}</h2>
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (accountId && actual) snapshot.mutate()
          }}
        >
          <label className="text-xs text-slate-500">
            {t('inv.account')}
            <select
              className="mt-1 block rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm text-slate-100"
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
            >
              <option value="">{t('inv.pick')}</option>
              {(accounts.data ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-500">
            {t('common.asset')}
            <input
              className="mt-1 block w-24 rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
              value={asset}
              onChange={(event) => setAsset(event.target.value.toUpperCase())}
            />
          </label>
          <label className="text-xs text-slate-500">
            {t('inv.actual')}
            <input
              className="num mt-1 block w-32 rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
              value={actual}
              onChange={(event) => setActual(event.target.value)}
            />
          </label>
          <button type="submit" className="rounded-md bg-slate-100 px-3 py-1.5 text-sm text-slate-900">
            {t('inv.write')}
          </button>
        </form>
        {snapshot.isError && (
          <p className="mt-2 text-xs text-loss">
            {snapshot.error instanceof Error ? snapshot.error.message : t('inv.fail')}
          </p>
        )}
        <ul className="mt-4 divide-y divide-surface-border text-sm">
          {(snapshots.data ?? []).map((row) => (
            <li key={row.id} className="flex justify-between py-2">
              <span className="text-slate-400">
                {row.asset_code} · {formatDateTime(row.as_of)}
              </span>
              <span className={row.drift && Number(row.drift) !== 0 ? 'num text-pending' : 'num text-slate-200'}>
                {t('inv.computed', { v: formatQty(row.computed_quantity) })}
                {row.actual_quantity != null ? t('inv.fact', { v: formatQty(row.actual_quantity) }) : ''}
                {row.drift != null && Number(row.drift) !== 0 ? t('inv.drift', { v: formatQty(row.drift) }) : ''}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
